import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Modal, Form, Row, Col, InputGroup, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiX, FiSearch, FiEye, FiTrash2 } from 'react-icons/fi';
import { toast } from "react-toastify";
import IconWord from "../../../../images/dashboard/icon-word.png";
import IconPdf from "../../../../images/dashboard/icon-pdf.png";
import IconExcel from "../../../../images/dashboard/icon-excel.png";
import image from "../../../../images/ai-robot.gif";
import { HiOutlineDotsVertical } from "react-icons/hi";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import apiConfig from "../../../credentials/apiConfig";
import { debounce } from 'lodash';
import ReactTextTransition from "react-text-transition";


const initialMessages = [
  "AI is analyzing your data. Please wait...",
  "Hang tight! Your analysis is in progress..."
];

const repeatMessages = [
  "Fetching your results. Almost there...",
  "Great things take time. Preparing your results now...",
  "Just a little longer. Thanks for your patience!"
];

// Define NoSearchResults component inside the same file for completeness
const NoSearchResults = ({ searchTerm, isSearching, onClearSearch }) => {
  if (isSearching) {
    return (
      <div className="text-center p-5">
        <div className="d-flex justify-content-center align-items-center flex-column">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Searching...</span>
          </div>
          <p className="mb-0">Searching for "{searchTerm}"...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center p-5">
      <div className="mb-4">
        <div className="d-flex justify-content-center">
          <div className="p-3 rounded-circle bg-light">
            <FiSearch size={36} className="text-muted" />
          </div>
        </div>
      </div>
      <h5 className="mb-2">No matching records found</h5>
      <p className="text-muted mb-4">
        We couldn't find any documents matching "{searchTerm}"
      </p>
      <Button
        variant="outline-primary"
        onClick={onClearSearch}
      >
        Clear Search
      </Button>
    </div>
  );
};

const UploadSection = ({
  addFiles,
  removeFile,
  generateAnalysis,
  updateAnalysisData,
  uploadedDocuments,
  setUploadedDocuments,
  selectedDocument,
  setSelectedDocument,
  setAnalysisGenerated,
  onRefreshDocumentList,
  files,
}) => {
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [clientId, setClientId] = useState("");
  const [documentBeingAnalyzed, setDocumentBeingAnalyzed] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [status, setStatus] = useState(null);


  // New state for delete confirmation modal
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);


  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [extraMessage, setExtraMessage] = useState("");

  useEffect(() => {
    let messageTimer, elapsedTimer;

    if (isPolling) {
      setCurrentMessageIndex(0);
      setElapsedSeconds(0);
      setExtraMessage("");

      messageTimer = setInterval(() => {
        setCurrentMessageIndex(prev => prev + 1);
      }, 5000);

      elapsedTimer = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }

    return () => {
      clearInterval(messageTimer);
      clearInterval(elapsedTimer);
    };
  }, [isPolling]);

  useEffect(() => {
    if (elapsedSeconds >= 300) {
      setExtraMessage("Thanks for your patience! Almost done.");
    } else if (elapsedSeconds >= 180) {
      setExtraMessage("Almost there. Please don’t close this tab.");
    } else if (elapsedSeconds >= 60) {
      setExtraMessage("Still working... Some files may take longer.");
    }
  }, [elapsedSeconds]);




  const getMessage = () => {
    if (currentMessageIndex < initialMessages.length) {
      return initialMessages[currentMessageIndex];
    } else {
      const loopIndex = (currentMessageIndex - initialMessages.length) % repeatMessages.length;
      return repeatMessages[loopIndex];
    }
  };


  // Define getFileNameFromUrl function first before using it
  // const getFileNameFromUrl = (url) => {
  //   if (!url) return "Unknown file";
  //   try {
  //     return decodeURIComponent(url.split('/').pop().split('?')[0]);
  //   } catch (e) {
  //     return url.split('/').pop().split('?')[0];
  //   }
  // };

  const getFileNameFromUrl = (url) => {
    if (!url) return "Unknown file";
    try {
      const decoded = decodeURIComponent(url.split('/').pop().split('?')[0]);
      // Remove the leading ID and dash (digits followed by a dash)
      return decoded.replace(/^\d+-/, '');
    } catch (e) {
      const fallback = url.split('/').pop().split('?')[0];
      return fallback.replace(/^\d+-/, '');
    }
  };



  // Initial fetch when client ID is available
  useEffect(() => {
    if (clientId && !isInitialized) {
      setIsInitialized(true); // Ensure only once
      onRefreshDocumentList?.();
    }
  }, [clientId, isInitialized]);


  // Polling mechanism for document analysis status
  useEffect(() => {

    const effectiveClientId = clientId || documentBeingAnalyzed?.clientId;


    if (!documentBeingAnalyzed || !documentBeingAnalyzed.fileKey || !clientId) return;

    // If document already has a summary, no need to poll
    if (documentBeingAnalyzed.summary && documentBeingAnalyzed.flaggedPoints) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    const interval = setInterval(async () => {
      try {
        const authToken = localStorage.getItem("authToken");

        // Using pathParameters format as shown in the backend code
        const url = `${apiConfig.apiBaseURL}/getDocumentStatus/${effectiveClientId}`;

        // Poll to check document analysis status
        const res = await axios.post(
          url,
          { fileKey: documentBeingAnalyzed.fileKey },
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Authorization": `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        // Check if we have a valid response with status
        if (res.data) {
          const { status, summary, flaggedPoints, failed } = res.data;
          setStatus(status);
          
          if (failed) {
           clearInterval(interval);
           setIsPolling(false);
           toast.error("AI classification failed. Please try again");
           return;
           }

          // Check if analysis is complete
          if (status === "completed" || (summary && flaggedPoints)) {
            clearInterval(interval);

            // Update document in the list with the new summary data
            const updatedDocuments = uploadedDocuments.map(doc => {
              if (doc.fileKey === documentBeingAnalyzed.fileKey) {
                return {
                  ...doc,
                  summary: summary || "",
                  flaggedPoints: flaggedPoints || "",
                  summaryGenerated: true
                };
              }
              return doc;
            });

            setUploadedDocuments(updatedDocuments);

            // Update selected document with new data
            const updatedSelectedDoc = {
              ...documentBeingAnalyzed,
              summary: summary || "",
              flaggedPoints: flaggedPoints || "",
              summaryGenerated: true
            };

            setSelectedDocument(updatedSelectedDoc);

            // IMPORTANT: Call updateAnalysisData directly
            updateAnalysisData(summary, flaggedPoints || "");

            setAnalysisGenerated(true);
            setIsPolling(false);
            setDocumentBeingAnalyzed(null);
            onRefreshDocumentList?.();
          }
        }
      } catch (error) {
        console.error("[Polling] Error during document status polling:", error);
        // Don't clear the interval on error - keep trying
        // But we might want to implement a retry limit
      }
    }, 5000); // Poll every 5 seconds

    return () => {
      clearInterval(interval);
      setIsPolling(false);
    };
  }, [documentBeingAnalyzed, clientId, uploadedDocuments]);


  const filteredDocuments = documents
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA; // Descending order (newest first)
    })
    .filter(doc => {
      if (!searchTerm) return true;

      const fileName = getFileNameFromUrl(doc.fileUrl || '').toLowerCase();
      const clientName = (doc.clientName || '').toLowerCase();
      const searchTermLower = searchTerm.toLowerCase();

      return fileName.includes(searchTermLower) || clientName.includes(searchTermLower);
    });

  // Fetch clients list for dropdown
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const authToken = localStorage.getItem("authToken");

        const response = await axios.get(
          `${apiConfig.apiBaseURL}/getAllClients`,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Authorization": `Bearer ${authToken}`,
              'Access-Control-Allow-Origin': '*',
              'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
              'Access-Control-Allow-Headers': 'Content-Type, Authorization, x-api-key'
            }
          }
        );

        if (response.data && response.data.success) {
          setClientsList(response.data.clients);

          setIsInitialized(true); // Mark as initialized after clients are fetched
        } else {
          console.log("API returned success: false or no clients found");
          setClientsList([]);
          setIsInitialized(true); // Mark as initialized even if no clients found
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClientsList([]);
        setIsInitialized(true); // Mark as initialized even if there was an error
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (files?.length > 0) {
      const backendDocs = files.filter(file => file.fileUrl);
      setUploadedDocuments(backendDocs);
      setSelectedDocument(null);
    }
  }, [files, setUploadedDocuments]);

  // Set documents state based on uploaded documents
  useEffect(() => {
    if (uploadedDocuments?.length > 0) {
      setDocuments(uploadedDocuments);
    }
  }, [uploadedDocuments]);

  // UploadSection.js - Fixed debouncedSearch function to handle client ID properly
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.trim() === '') {
        // Empty search should refresh the full document list and auto-select latest
        setIsSearching(true);

        try {
          // Call refresh function if available
          if (typeof onRefreshDocumentList === 'function') {
            await onRefreshDocumentList();
          } else {
            // Fallback to direct API call if needed
            const authToken = localStorage.getItem("authToken");
            const response = await axios.post(
              `${apiConfig.apiBaseURL}/getDocumentSummary`,
              { searchString: "" }, // Empty string to get all documents
              {
                headers: {
                  "x-api-key": apiConfig["x-api-key"],
                  "Authorization": `Bearer ${authToken}`,
                  "Content-Type": "application/json"
                },
                timeout: 30000
              }
            );

            if (response.data.success) {
              const allDocs = response.data.data || [];
              const processedDocs = allDocs.map(doc => ({
                ...doc,
                clientId: doc.clientId || doc._id,
                fileType: doc.type || 'boardMeetingMinutes',
                clientName: doc.clientName || ''
              }));

              setDocuments(processedDocs);
              setUploadedDocuments(processedDocs);

              // Auto-select latest document after clearing search
              if (processedDocs.length > 0) {
                const latestDocument = processedDocs[0];

                setSelectedDocument(latestDocument);

                if (latestDocument?.summary && latestDocument?.flaggedPoints) {
                  handleDocumentClick(latestDocument);
                }
              }

            }
          }
        } catch (error) {
          console.error("Error refreshing documents after search clear:", error);
        } finally {
          setNoResultsFound(false);
          setIsSearching(false);
        }
        return;
      }

      // FIXED: Removed client ID requirement for search - let backend handle it
      try {
        setIsSearching(true);

        const authToken = localStorage.getItem("authToken");
        const response = await axios.post(
          `${apiConfig.apiBaseURL}/getDocumentSummary`,
          { searchString: query }, // Pass the search query to the API
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Authorization": `Bearer ${authToken}`,
              "Content-Type": "application/json"
            },
            timeout: 30000
          }
        );

        if (response.data.success) {
          // NEW: Direct processing of the flat array structure
          const allDocs = response.data.data || [];

          // Process each document to ensure consistent structure
          const processedDocs = allDocs.map(doc => ({
            ...doc,
            clientId: doc.clientId || doc._id,
            fileType: doc.type || 'boardMeetingMinutes', // Use 'type' field
            clientName: doc.clientName || ''
          }));

          // Update state with search results
          setDocuments(processedDocs);
          setUploadedDocuments(processedDocs);

          // Check if we got any results
          setNoResultsFound(query.trim() !== "" && processedDocs.length === 0);

        } else {
          toast.error(response.data.message || "Failed to search documents");
          setNoResultsFound(true);
        }
      } catch (err) {
        console.error("Error searching documents:", err);
        toast.error("Failed to search documents. Please try again.");
        setNoResultsFound(true);
      } finally {
        setIsSearching(false);
      }
    }, 500), // 500ms debounce time
    [] // Removed clientId dependencies that were causing issues
  );

  // Alternative approach - if backend requires client context, use this version:
  const debouncedSearchWithClientContext = useCallback(
    debounce(async (query) => {
      if (!query || query.trim() === '') {
        // Empty search logic remains the same...
        setIsSearching(true);
        try {
          if (typeof onRefreshDocumentList === 'function') {
            await onRefreshDocumentList();
          }
        } catch (error) {
          console.error("Error refreshing documents after search clear:", error);
        } finally {
          setNoResultsFound(false);
          setIsSearching(false);
        }
        return;
      }

      try {
        setIsSearching(true);

        // Get client context from multiple sources without requiring it
        let clientContext = null;

        // Try to get from current state
        if (clientId || selectedClientId) {
          clientContext = clientId || selectedClientId;
        }

        // Try to get from uploaded documents (if any exist)
        if (!clientContext && uploadedDocuments && uploadedDocuments.length > 0) {
          clientContext = uploadedDocuments[0].clientId;
        }

        // Try to get from localStorage as fallback
        if (!clientContext) {
          clientContext = localStorage.getItem("clientId");
        }

        // Try to get from URL params as last resort
        if (!clientContext) {
          const urlParams = new URLSearchParams(window.location.search);
          clientContext = urlParams.get('clientId');
        }

        const authToken = localStorage.getItem("authToken");

        // Prepare request payload - include clientId only if available
        const payload = { searchString: query };
        if (clientContext) {
          payload.clientId = clientContext;
        }

        const response = await axios.post(
          `${apiConfig.apiBaseURL}/getDocumentSummary`,
          payload,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Authorization": `Bearer ${authToken}`,
              "Content-Type": "application/json"
            },
            timeout: 30000
          }
        );

        if (response.data.success) {
          const allDocs = response.data.data || [];
          const processedDocs = allDocs.map(doc => ({
            ...doc,
            clientId: doc.clientId || doc._id,
            fileType: doc.type || 'boardMeetingMinutes',
            clientName: doc.clientName || ''
          }));

          setDocuments(processedDocs);
          setUploadedDocuments(processedDocs);
          setNoResultsFound(query.trim() !== "" && processedDocs.length === 0);
        } else {
          toast.error(response.data.message || "Failed to search documents");
          setNoResultsFound(true);
        }
      } catch (err) {
        console.error("Error searching documents:", err);

        // More specific error handling
        if (err.response?.status === 400 && err.response?.data?.message?.includes("Client ID")) {
          toast.error("Unable to search - please refresh the page and try again");
        } else {
          toast.error("Failed to search documents. Please try again.");
        }
        setNoResultsFound(true);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    [uploadedDocuments] // Only depend on uploadedDocuments for client context
  );

  // Apply the search when searchTerm changes
  useEffect(() => {

    // Cleanup function to cancel pending debounced calls
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Clear search handler
  const handleClearSearch = () => {
    // First clear the search term
    setSearchTerm('');
    setNoResultsFound(false);

    // Then explicitly trigger document refresh
    // Using setTimeout to ensure state is updated before refresh
    setTimeout(() => {
      debouncedSearch.cancel(); // Cancel any pending debounced searches
      onRefreshDocumentList?.()
    }, 0);
  };

  // Modified delete handler to open the confirmation modal
  const handleDeleteDocument = (docToDelete) => {
    setDocumentToDelete(docToDelete);
    setShowDeleteConfirmModal(true);
    setOpenDropdownIndex(null); // Close the dropdown
  };

  // New function to handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      setIsLoading(true);

      // Use the document's own clientId
      const documentClientId = documentToDelete.clientId;

      if (!documentClientId) {
        toast.error("Error: Document is missing client ID information");
        setShowDeleteConfirmModal(false);
        setIsLoading(false);
        return;
      }

      // Call soft delete API
      const authToken = localStorage.getItem("authToken");
      const response = await axios.put(
        `${apiConfig.apiBaseURL}/softDeleteUploadedDocument`,
        {
          clientId: documentClientId,
          fileKey: documentToDelete.fileKey
        },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Document deleted successfully");

        // Update local state to remove the deleted document
        setDocuments(prevDocs =>
          prevDocs.filter(doc => doc.fileKey !== documentToDelete.fileKey)
        );

        // If the deleted document was selected, clear the selection
        if (selectedDocument && selectedDocument.fileKey === documentToDelete.fileKey) {
          setSelectedDocument(null);
        }
        onRefreshDocumentList?.()
      } else {
        toast.error(response.data.message || "Failed to delete document");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error(error.response?.data?.message || "Error deleting document. Please try again.");
    } finally {
      setIsLoading(false);
      setShowDeleteConfirmModal(false);
      setDocumentToDelete(null);
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <img src={IconPdf} className='icon-pdf my-auto' width={40} height={40} alt="PDF icon" />;
      case 'doc':
      case 'docx':
        return <img src={IconWord} className='icon-pdf my-auto' width={40} height={40} alt="Word icon" />;
      default:
        return <img src={IconExcel} className='icon-pdf my-auto' width={40} height={40} alt="Excel icon" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length > 0) {
      const newFile = Object.assign(acceptedFiles[0], {
        preview: URL.createObjectURL(acceptedFiles[0])
      });
      setPendingFile(newFile);
      setShowModal(true);
    }

    if (rejectedFiles.length > 0) {
      setRejectedFiles(rejectedFiles);

      rejectedFiles.forEach(({ file, errors }) => {
        if (errors[0]?.code === 'file-too-large') {
          toast.error(`${file.name} is too large. Max size is 10MB`);
        } else if (errors[0]?.code === 'file-invalid-type') {
          toast.error(`${file.name} has an invalid file type. Only PDF and DOCX are accepted`);
        } else {
          toast.error(`${file.name} could not be uploaded: ${errors[0]?.message || 'Unknown error'}`);
        }
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      // 'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize: 10485760, // 10MB
    validator: (file) => {
      // Check for duplicates in local files
      if (files.some(existingFile => existingFile.name === file.name)) {
        return {
          code: 'duplicate-file',
          message: 'This file has already been uploaded'
        };
      }

      // Check for duplicates in uploaded documents
      if (uploadedDocuments.some(doc => getFileNameFromUrl(doc.fileUrl) === file.name)) {
        return {
          code: 'duplicate-file',
          message: 'This file has already been uploaded'
        };
      }

      return null;
    }
  });

  const handleCloseModal = () => {
    setShowModal(false);
    if (pendingFile) {
      URL.revokeObjectURL(pendingFile.preview);
      setPendingFile(null);
    }
    setSelectedDocumentType('');
    setValidationAttempted(false);
  };

  const waitForDocumentReady = async (fileKey, clientId, maxAttempts = 5, interval = 2000) => {
    let attempts = 0;
    const authToken = localStorage.getItem("authToken");

    while (attempts < maxAttempts) {
      try {
        const res = await axios.post(
          `${apiConfig.apiBaseURL}/getDocumentStatus/${clientId}`,
          { fileKey },
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Authorization": `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (res.data?.status === "pending" || res.data?.summary || res.data?.flaggedPoints) {
          return true;
        }
      } catch (err) {
        console.warn("Waiting for document to be ready...", err.message);
      }
      attempts++;
      await new Promise(resolve => setTimeout(resolve, interval));
    }

    return false;
  };


  const handleUpload = async () => {
    setValidationAttempted(true);

    if (!selectedClientId) {
      toast.error("Please select a client - this field is required");
      return;
    }

    if (!selectedDocumentType) {
      toast.error("Please select a document type - this field is required");
      return;
    }

    if (!pendingFile) {
      toast.error("No file selected");
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("clientId", selectedClientId); // Use clientId parameter name for document upload
      formData.append("fileType", selectedDocumentType);
      formData.append("file", pendingFile);

      const authToken = localStorage.getItem("authToken");
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/documentsummaryUploadDocument`,
        formData,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${authToken}`,
            // Add CORS headers for local development
            'Access-Control-Allow-Origin': '*'
          },
          // Add timeout to prevent hanging requests
          timeout: 30000
        }
      );

      setClientId(selectedClientId || document.clientId);

      if (response.data.success) {
        // Handle the new response structure
        if (response.data.uploadedEntries && Array.isArray(response.data.uploadedEntries)) {
          // Add the newly uploaded document to the existing list
          const uploadedDoc = response.data.uploadedEntries?.[0];

          if (uploadedDoc?.fileKey) {
            const isReady = await waitForDocumentReady(uploadedDoc.fileKey, selectedClientId);
            if (isReady) {
              // Auto-select and analyze the newly uploaded document
              setSelectedDocument(uploadedDoc);
              handleAnalyzeDocument(uploadedDoc, { stopPropagation: () => { } });
            } else {
              toast.warn("Document not ready for analysis yet. Try again shortly.");
            }
          }

        } else {
          // If uploadedEntries is not available, refresh the list and auto-select latest
          console.log("No uploadedEntries in response, refreshing document list");
          onRefreshDocumentList?.();
        }
      } else {
        toast.error(response.data.message || "Upload failed");
      }
    } catch (err) {
      console.error("API Error:", err);
      toast.error(err.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleDocumentClick = (document) => {
    // Ensure fileKey is properly set
    if (!document.fileKey && document.fileUrl) {
      // Extract fileKey from fileUrl if needed
      const urlParts = document.fileUrl.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0];
      document.fileKey = fileName;
    }

    setSelectedDocument(document);

    // Check if the document has a generated summary
    if (document.summaryGenerated === true || (document.summary && document.summary.trim() !== "")) {
      // Process the flagged points data, handling different possible formats
      let flaggedPointsText = "";

      if (document.flaggedPoints) {
        if (Array.isArray(document.flaggedPoints)) {
          flaggedPointsText = document.flaggedPoints.join('\n');
        } else if (typeof document.flaggedPoints === 'string') {
          flaggedPointsText = document.flaggedPoints;
        } else {
          // For object or other types, convert to string
          flaggedPointsText = JSON.stringify(document.flaggedPoints);
        }
      }

      // Update analysis data with document's summary and flagged points
      updateAnalysisData(document.summary, flaggedPointsText);

      // Show the analysis panel
      generateAnalysis();
    } else {
      // For documents without summaries, show a clear message and option to analyze
      updateAnalysisData(null, null); // Reset analysis data
      setAnalysisGenerated(false); // This should be passed from the parent
      toast.info("This document hasn't been analyzed yet. Click 'Analyze' to generate a summary.");
    }
  };

  const handleAnalyzeDocument = async (document, e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation(); // prevent click event bubbling
    }

    if (!document.fileKey) {
      toast.error("Document key is missing");
      return;
    }

    if (!isInitialized) {
      toast.info("Still initializing. Please wait a moment and try again.");
      return;
    }

    try {
      setIsLoading(true);
      setClientId(selectedClientId || document.clientId);
      // Set the document being analyzed
      setDocumentBeingAnalyzed(document);

      // Set the selected document for UI
      setSelectedDocument(document);

      // If analysis already exists, display it immediately
      if (document.summary && document.flaggedPoints) {
        let flaggedPointsText;
        if (typeof document.flaggedPoints === 'string') {
          flaggedPointsText = document.flaggedPoints;
        } else if (Array.isArray(document.flaggedPoints)) {
          flaggedPointsText = document.flaggedPoints.join('\n');
        } else {
          flaggedPointsText = JSON.stringify(document.flaggedPoints);
        }

        updateAnalysisData(document.summary, flaggedPointsText);
        generateAnalysis();
        return;
      }

      // If document has clientId and fileType, it's from the list — re-trigger analysis
      if (document.clientId && (document.fileType || document.type)) {
        const authToken = localStorage.getItem("authToken");
        const formData = new FormData();
        formData.append("clientId", document.clientId);
        formData.append("fileKey", document.fileKey);
        formData.append("fileType", document.fileType || document.type);
        formData.append("reAnalyze", "true");

        await axios.post(
          `${apiConfig.apiBaseURL}/documentsummaryUploadDocument`,
          formData,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Content-Type": "multipart/form-data",
              "Authorization": `Bearer ${authToken}`,
            },
            timeout: 30000,
          }
        );
        toast.info("Analysis started. Waiting for results...");
      }

      generateAnalysis(document);
    } catch (err) {
      console.error("Error handling document analysis:", err);
      toast.error("Failed to process document analysis. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    return () => {
      files.forEach(file => URL.revokeObjectURL(file.preview));
      if (pendingFile) {
        URL.revokeObjectURL(pendingFile.preview);
      }
    };
  }, [files, pendingFile]);

  const totalFiles = uploadedDocuments.length + files.length;


  return (
    <Card className='h-100 upload-docs' style={{ border: '1px solid #ECEEF5' }}>
      <Card.Body>
        <h4 className="mb-3 fw-bold" style={{ fontSize: '20px' }}>Upload Documents</h4>

        <div
          {...getRootProps()}
          className={`dropzone rounded p-4 text-center mb-3 ${isDragActive ? 'bg-light-blue border-primary' : ''}`}
        >
          <input {...getInputProps()} />

          <div className="text-center">
            <div className="d-flex justify-content-center mb-3">
              <FiUpload size={28} className={isDragActive ? "text-primary" : "text-secondary"} />
            </div>
            {isDragActive ? (
              <p className="mb-0 text-primary">Drop your files here...</p>
            ) : (
              <>
                <p className="mb-0 fw-bold">Drag & Drop or <span className="text-primary fw-bold">Choose file</span> to upload</p>
                <p className="small text-light mb-0">PDF, DOCX (max. 10MB each)</p>
                <p className="small text-light mb-0">{totalFiles} Files Uploaded</p>
              </>
            )}
          </div>
        </div>

        <div className="mb-4">
          <InputGroup className="w-100 mb-3">
            <InputGroup.Text id="search-addon" className="bg-white border-end-0">
              <FiSearch size={16} className={isSearching ? "text-primary" : "text-muted"} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search documents"
              aria-label="Search documents"
              aria-describedby="search-addon"
              value={searchTerm}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerm(newValue);
                // Always trigger search, even when empty
                debouncedSearch(newValue);
              }}
              className="border-start-0"
            />
            {searchTerm && (
              <InputGroup.Text
                className="bg-white border-start-0"
                onClick={handleClearSearch}
                style={{ cursor: 'pointer' }}
              >
                <FiX size={16} className="text-muted" />
              </InputGroup.Text>
            )}
          </InputGroup>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredDocuments.length > 0 && !noResultsFound ? (
              filteredDocuments.map((document, index) => {
                const fileName = getFileNameFromUrl(document.fileUrl);

                let documentType;
                if (document.fileType) {
                  documentType = document.fileType === 'boardMeetingMinutes' ? 'Board Meeting Minutes' : 'Debt Agreement';
                } else {
                  documentType = document.hasOwnProperty('boardMeetingDate') ? 'Board Meeting Minutes' : 'Debt Agreement';
                }

                // Format the date and time
               const documentDate = new Date(document.uploadedAt);
                const formattedDate = documentDate.toLocaleDateString();
                const formattedTime = documentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                // Check if this document is the selected one by comparing file URLs
                const isSelected = selectedDocument?.fileUrl === document.fileUrl;
                const isChecked = selectedFiles.includes(document.fileUrl);
                const hasAnalysis = document.summaryGenerated === true || (document.summary && document.flaggedPoints);

                return (
                  <div
                    key={`uploaded-${index}`}
                    className={`d-flex align-items-center p-3 border rounded mb-2 ${isSelected ? 'active-document bg-primary bg-opacity-10 border-primary' : ''
                      }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleDocumentClick(document)}
                  >
                    <div className="d-flex justify-content-between align-items-start w-100" style={{ minWidth: 0 }}>
                      <div className="d-flex flex-grow-1 align-items-start text-primary" style={{ minWidth: 0, overflow: 'hidden' }}>
                        {getFileIcon(fileName)}
                        <div className="ms-2 flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
                          <div className="fw-bold text-truncate" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '700' }}>
                            {fileName.replace(/^\d+-PAR_Report/, 'PAR_Report')}
                          </div>
                          <div
                            className="small"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '700', color: '#677289', fontSize: '12px' }}
                          >
                            {document.clientName || 'N/A'} &nbsp;•&nbsp;
                            {documentType} &nbsp;•&nbsp;
                            {formattedDate} {formattedTime}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-start flex-shrink-0 ms-0 position-relative my-auto">
                        {hasAnalysis ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View</Tooltip>}
                          >
                            <Button
                              variant="outline-light"
                              size="sm"
                              className="no-hover border-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDocumentClick(document);
                              }}
                            >
                              <FiEye size={16} />
                            </Button>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => handleAnalyzeDocument(document, e)}
                          >
                            Analyze
                          </Button>
                        )}

                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>More Options</Tooltip>}
                        >
                          <div
                            className="ms-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownIndex(openDropdownIndex === index ? null : index);
                            }}
                            style={{ cursor: 'pointer' }}
                          >

                            <HiOutlineDotsVertical className="text-light" size={24} />
                          </div>
                        </OverlayTrigger>

                        {openDropdownIndex === index && (
                          <div
                            className="position-absolute bg-white shadow rounded py-2"
                            style={{ top: '100%', right: 0, zIndex: 999 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className="dropdown-item d-flex align-items-center text-danger"
                              style={{ cursor: 'pointer', padding: '6px 16px' }}
                              onClick={() => handleDeleteDocument(document)}
                            >
                              <FiTrash2 className="me-2" /> Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-4">
                {searchTerm ? (
                  <NoSearchResults
                    searchTerm={searchTerm}
                    isSearching={isSearching}
                    onClearSearch={handleClearSearch}
                  />
                ) : (
                  <div className="text-center p-4 text-muted">
                    No documents uploaded yet
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* AI Analysis Loading Modal - FIXED */}
        {isPolling && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "420px",
                backgroundColor: "#fff",
                borderRadius: "16px",
                padding: "40px 20px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                textAlign: "center",
              }}
            >
              <img
                src={image}
                alt="Processing"
                style={{
                  width: "120px",
                  marginBottom: "20px",
                }}
              />
              <h5 style={{ fontWeight: 500, fontSize: "18px", color: "#333" }}>
                The AI is working on it.
              </h5>

              <div className='d-flex justify-content-center'>
                <ReactTextTransition>
                  {getMessage()}
                </ReactTextTransition>
              </div>
              {extraMessage && (
                <p style={{ fontSize: "14px", color: "#999", marginTop: "10px" }}>
                  {extraMessage}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Upload Document Type Modal */}
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title className='fw-bold'>Select Document Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {pendingFile && (
              <div className="mb-3">
                <div className="d-flex align-items-center border rounded p-2 mb-3">
                  <div className="me-2">
                    {getFileIcon(pendingFile.name)}
                  </div>
                  <div>
                    <div>{pendingFile.name}</div>
                    <div className="text-muted small">{formatFileSize(pendingFile.size)}</div>
                  </div>
                </div>
              </div>
            )}

            <Form className='credential-page'>
              {/* Client dropdown */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Select Client <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  required
                  isInvalid={validationAttempted && !selectedClientId}
                >
                  <option value="">Please select a client</option>
                  {clientsList.map((client) => (
                    <option key={client.clientId || client._id} value={client.clientId || client._id}>
                      {client.clientName}
                    </option>
                  ))}
                </Form.Select>
                {showModal && !selectedClientId && (
                  <Form.Control.Feedback type="invalid">
                    Client selection is required
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Label>Document Type *</Form.Label>
              <div className="mb-3">
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="radio"
                      id="document-type-board-meeting"
                      name="documentType"
                      label="Board Meeting Minutes"
                      value="boardMeetingMinutes"
                      onChange={() => setSelectedDocumentType('boardMeetingMinutes')}
                      checked={selectedDocumentType === 'boardMeetingMinutes'}
                      className="mb-2"
                      isInvalid={validationAttempted && !selectedDocumentType}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="radio"
                      id="document-type-debt-agreement"
                      name="documentType"
                      label="Debt Agreement"
                      value="debtAgreements"
                      onChange={() => setSelectedDocumentType('debtAgreements')}
                      checked={selectedDocumentType === 'debtAgreements'}
                      isInvalid={validationAttempted && !selectedDocumentType}
                    />
                  </Col>
                </Row>
                {validationAttempted && !selectedDocumentType && (
                  <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                    Document type selection is required
                  </div>
                )}
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-primary" className='px-3' onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" className='px-3' onClick={handleUpload} disabled={isLoading} >
              {isLoading ? "Loading..." : "Analyze"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteConfirmModal}
          onHide={() => {
            setShowDeleteConfirmModal(false);
            setDocumentToDelete(null);
          }}
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title className='fw-bold'>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {documentToDelete && (
              <div>
                <p>Are you sure you want to delete this document?</p>
                <div className="d-flex align-items-center border rounded p-2 mt-3">
                  <div className="me-2">
                    {getFileIcon(getFileNameFromUrl(documentToDelete.fileUrl))}
                  </div>
                  <div>
                    <div className="fw-bold">{getFileNameFromUrl(documentToDelete.fileUrl)}</div>
                    <div className="text-muted small">
                      {documentToDelete.fileType === 'boardMeetingMinutes' ? 'Board Meeting Minutes' : 'Debt Agreement'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-primary"
              className='px-3'
              onClick={() => {
                setShowDeleteConfirmModal(false);
                setDocumentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className='px-3 text-white'
              onClick={handleConfirmDelete}
              disabled={isLoading}
            >
              {isLoading ? "Deleting..." : "Yes, Delete"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default UploadSection;